import React, { useEffect, useRef, useState } from "react";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "src/components/ui/navigation-menu";
import { ArrowUpLeft, ChevronsDown, Globe, Menu, Sun } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import MainButton from "../../../components/MainButton";
import { useScroll, useTransform, motion } from "framer-motion";
import { useLanguage } from "../../../context/LanguageContext";
import { useAuth } from "../../../context/AuthContext";
import SelectLanguage from "../../../components/SelectLanguage";
import { SelectTheme } from "../../../components/SelectTheme";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "src/components/ui/sheet";
import { Button } from "../../../components/ui/button";
import { Skeleton } from "src/components/ui/skeleton";

export default function MainHeader({ settings, loading }) {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { user, logout } = useAuth();

  const isAdmin = user?.role === "admin"; // Check if the user is an admin
  const isTeacher = user?.role === "teacher"; // Check if the user is an admin

  // Navigate sidebar
  const [open, setOpen] = useState(false);

  const handleClose = () => setOpen(false);
  const handleNavigate = (path) => {
    navigate(path);
    handleClose();
  };

  return (
    <>
      <div className="bg-gradient-to-t from-blue-700 to-blue-500 text-white">
        <div className="max-w-7xl mx-auto px-4 flex justify-between items-center h-10">
          <div className="flex items-center gap-3">
            <SelectLanguage>
              <button className="p-1 rounded-full transition-colors focus-visible:outline-none">
                <Globe className="h-5 w-5" />
              </button>
            </SelectLanguage>
            <SelectTheme>
              <button className="p-1 rounded-full transition-colors focus-visible:outline-none">
                <Sun className="h-5 w-5" />
              </button>
            </SelectTheme>
          </div>
          {loading ? (
            <div className="flex items-center gap-4">
              <Skeleton className="w-24 h-5" />
              <Skeleton className="w-24 h-5" />
            </div>
          ) : (
            <div className="flex items-center gap-4">
              <span className="text-sm">{settings.phone_site}</span>
              <span className="text-sm">{settings.email_site}</span>
            </div>
          )}
        </div>
      </div>
      <header className="py-4 shadow-sm sticky top-0 left-0 right-0 z-10 bg-background/50 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-4 flex items-center justify-between">
          {/* Desktop */}
          <div className="flex items-center justify-between w-full">
            <div className="text-2xl font-bold text-right">
              {loading ? (
                <Skeleton className="w-32 h-10" />
              ) : (
                <Link to="/" className="">
                  {settings.name_site}
                </Link>
              )}
            </div>

            <NavigationMenu dir="rtl" className="hidden lg:block">
              <NavigationMenuList className="flex gap-6">
                <NavigationMenuItem>
                  <Link to="/" legacyBehavior passto>
                    <NavigationMenuLink className="hover:text-blue-600 transition-colors">
                      الرئيسية
                    </NavigationMenuLink>
                  </Link>
                </NavigationMenuItem>
                <NavigationMenuItem>
                  <Link to="/courses" legacyBehavior passto>
                    <NavigationMenuLink className="hover:text-blue-600 transition-colors">
                      الدورات
                    </NavigationMenuLink>
                  </Link>
                </NavigationMenuItem>
                <NavigationMenuItem>
                  <Link to="/faq" legacyBehavior passto>
                    <NavigationMenuLink className="hover:text-blue-600 transition-colors">
                      الأسئلة الشائعة
                    </NavigationMenuLink>
                  </Link>
                </NavigationMenuItem>
                <NavigationMenuItem>
                  <Link to="/teachers" legacyBehavior passto>
                    <NavigationMenuLink className="hover:text-blue-600 transition-colors">
                      المعلمون
                    </NavigationMenuLink>
                  </Link>
                </NavigationMenuItem>
                <NavigationMenuItem>
                  <Link to="/about-us" legacyBehavior passto>
                    <NavigationMenuLink className="hover:text-blue-600 transition-colors">
                      من نحن
                    </NavigationMenuLink>
                  </Link>
                </NavigationMenuItem>
                <NavigationMenuItem>
                  <Link to="/students-rates" legacyBehavior passto>
                    <NavigationMenuLink className="hover:text-blue-600 transition-colors">
                      تقييمات الطلاب
                    </NavigationMenuLink>
                  </Link>
                </NavigationMenuItem>
                <NavigationMenuItem>
                  <Link to="/survays" legacyBehavior passto>
                    <NavigationMenuLink className="hover:text-blue-600 transition-colors">
                      الاستبيانات
                    </NavigationMenuLink>
                  </Link>
                </NavigationMenuItem>
              </NavigationMenuList>
            </NavigationMenu>

            <div className="hidden gap-3 lg:flex">
              {user ? (
                <>
                  <MainButton
                    title={"حسابي"}
                    className={
                      "bg-transparent text-primary border-2 border-blue-600 shadow-sm"
                    }
                    onClick={() =>
                      navigate(
                        isAdmin
                          ? "/admin/overview"
                          : isTeacher
                          ? "/teacher/courses"
                          : "dashboard"
                      )
                    }
                  />
                  <MainButton title={"الخروج"} onClick={logout} />
                </>
              ) : (
                <>
                  <MainButton
                    title={"تسجيل دخول"}
                    className={
                      "bg-transparent text-primary border-2 border-blue-600 shadow-sm"
                    }
                    onClick={() => navigate("/login")}
                  />
                  <MainButton
                    title={"إنشاء حساب"}
                    onClick={() => navigate("/register")}
                  />
                </>
              )}
            </div>
          </div>

          {/* Mobile */}
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button className="p-2 block lg:hidden">
                <Menu />
              </button>
            </SheetTrigger>
            <SheetContent side="left" dir="rtl">
              <SheetHeader>
                <SheetTitle>علي أكاديمي</SheetTitle>
              </SheetHeader>
              <nav className="flex flex-col space-y-4 mt-4 rtl">
                <Link
                  to="/"
                  className="hover:text-blue-600"
                  onClick={handleClose}
                >
                  الرئيسية
                </Link>
                <Link
                  to="/courses"
                  className="hover:text-blue-600"
                  onClick={handleClose}
                >
                  الدورات
                </Link>
                <Link
                  to="/faq"
                  className="hover:text-blue-600"
                  onClick={handleClose}
                >
                  الأسئلة الشائعة
                </Link>
                <Link
                  to="/teachers"
                  className="hover:text-blue-600"
                  onClick={handleClose}
                >
                  المعلمون
                </Link>
                <Link
                  to="/about-us"
                  className="hover:text-blue-600"
                  onClick={handleClose}
                >
                  من نحن
                </Link>
                <Link
                  to="/students-rates"
                  className="hover:text-blue-600"
                  onClick={handleClose}
                >
                  تقييمات الطلاب
                </Link>
                <Link
                  to="/survays"
                  className="hover:text-blue-600"
                  onClick={handleClose}
                >
                  الاستبيانات
                </Link>

                <div className="mt-6 flex flex-col space-y-2">
                  {user ? (
                    <>
                      <MainButton
                        title={"حسابي"}
                        className="bg-transparent text-primary border-2 border-blue-600 shadow-sm w-full lg:w-fit"
                        onClick={() =>
                          handleNavigate(
                            isAdmin
                              ? "/admin/overview"
                              : isTeacher
                              ? "/teacher/courses"
                              : "/dashboard"
                          )
                        }
                      />
                      <MainButton
                        title={"الخروج"}
                        onClick={logout}
                        className="w-full lg:w-fit"
                      />
                    </>
                  ) : (
                    <>
                      <MainButton
                        title={"تسجيل دخول"}
                        className="bg-transparent text-primary border-2 border-blue-600 shadow-sm w-full lg:w-fit"
                        onClick={() => handleNavigate("/login")}
                      />
                      <MainButton
                        title={"إنشاء حساب"}
                        onClick={() => handleNavigate("/register")}
                        className="w-full lg:w-fit"
                      />
                    </>
                  )}
                </div>
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </header>
    </>
  );
}
